/**
 * town_props_loader.ts — the TOWN LIFE wave's generated props (2026-09-24): the bounty guild's terminal, the
 * contract board, the camp log bench and the firework rack. Hunyuan3D-2.1 meshes (tools/asset-pipeline/
 * gen-terminals-hf.py) cooked by cook-ceremony-props.mjs into the static side-channel public/props/terminals/.
 *
 * LOOKED AT (render-glb-views, 2026-09-24): the contract board's notices face model +Z; the terminal's WANTED
 * screen faces model −X — `yaw` turns each so its FRONT faces a station's +Z (the way the station faces).
 * A key not landed returns null: the caller keeps its procedural stand-in (the self-heal base).
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex/core/App.js';
import { Logger } from 'enginex/core/Logger.js';
import { fitTransform } from '../../lib/ceremony_props.js';

export const TOWN_PROP_DIR = 'props/terminals';
/** Target height (m) and the model yaw that turns its front onto +Z. */
export const TOWN_PROPS: Record<string, { targetH: number; yaw: number }> = {
  bounty_terminal: { targetH: 2.15, yaw: Math.PI / 2 },
  contract_board: { targetH: 2.45, yaw: 0 },
  // LOOKED AT (render-glb-views, 2026-09-24): the bank terminal's screen and deposit slot face model +Z.
  bank_terminal: { targetH: 2.05, yaw: 0 },
  camp_log_bench: { targetH: 0.55, yaw: 0 },
  firework_rack: { targetH: 1.05, yaw: 0 },
};

interface Master { object: THREE.Object3D; fit: { scale: number; dx: number; dy: number; dz: number }; size: THREE.Vector3 }
const masters = new Map<string, Master>();
const stats = { manifest: false, keys: 0, loaded: 0, failed: 0, error: '' };
let started = false;

export function townPropStats(): typeof stats & { ready: string[] } { return { ...stats, ready: [...masters.keys()] }; }

export async function prefetchTownProps(app: App): Promise<void> {
  if (started) return;
  started = true;
  const assets = app.getModule('AssetModule') as { loadUrl?(url: string): Promise<THREE.Object3D> } | undefined;
  if (!assets?.loadUrl) { stats.error = 'no AssetModule'; return; }
  let manifest: { props?: Record<string, { file: string }> };
  try {
    const res = await fetch(new URL(`${TOWN_PROP_DIR}/manifest.json`, document.baseURI).toString(), { cache: 'no-cache' });
    if (!res.ok) { stats.error = `manifest ${res.status}`; return; }
    manifest = await res.json();
  } catch (e) { stats.error = `manifest: ${String((e as Error)?.message ?? e)}`; return; }
  stats.manifest = true;
  const entries = Object.entries(manifest.props ?? {}).filter(([k]) => k in TOWN_PROPS);
  stats.keys = entries.length;
  for (const [key, ent] of entries) {
    try {
      const obj = await assets.loadUrl(new URL(`${TOWN_PROP_DIR}/${ent.file}`, document.baseURI).toString());
      obj.traverse((o) => { o.userData.sharedAsset = true; });
      // Turn the model's front onto +Z BEFORE measuring, so the fit's bottom-centre is the turned model's.
      const turned = new THREE.Group();
      turned.add(obj);
      obj.rotation.y = TOWN_PROPS[key].yaw;
      turned.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(turned);
      const fit = fitTransform(box, TOWN_PROPS[key].targetH);
      masters.set(key, { object: turned, fit, size: box.getSize(new THREE.Vector3()).multiplyScalar(fit.scale) });
      stats.loaded++;
    } catch (e) {
      stats.failed++;
      Logger.warn('TownProps', `${key}: ${String((e as Error)?.message ?? e)}`);
    }
  }
  Logger.info('TownProps', `${stats.loaded}/${stats.keys} town props ready`);
}

/** A fitted clone (origin = its bottom-centre, front along +Z), or null while the key has not landed. */
export function townProp(key: string): THREE.Group | null {
  const m = masters.get(key);
  if (!m) return null;
  const g = new THREE.Group();
  g.name = `town:${key}`;
  g.userData.sharedAsset = true;
  const clone = m.object.clone(true);
  clone.traverse((o) => { o.userData.sharedAsset = true; if ((o as THREE.Mesh).isMesh) { o.castShadow = true; o.receiveShadow = true; } });
  clone.scale.setScalar(m.fit.scale);
  clone.position.set(m.fit.dx, m.fit.dy, m.fit.dz);
  g.add(clone);
  return g;
}

/** The fitted footprint (x, y, z metres) of a landed prop, for its collider. */
export function townPropSize(key: string): THREE.Vector3 | null { return masters.get(key)?.size.clone() ?? null; }
